import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import './LocalClusterConfig.css';

function LocalClusterConfig({ assistant, onUpdate, accessToken }) {
    const { t: tConfig } = useTranslation('assistantConfiguration');

    const config = assistant?.local_cluster_config || { api_key: '', model: '' };

    // localModels: array de { id, vision }
    const [localModels, setLocalModels] = useState([]);
    const [isFetchingModels, setIsFetchingModels] = useState(false);
    const [fetchError, setFetchError] = useState(null);
    const [showApiKey, setShowApiKey] = useState(false);

    const handleApiKeyChange = (value) => {
        onUpdate('local_cluster_config', { ...config, api_key: value });
    };

    const handleModelChange = (value) => {
        const visionCapable = (localModels.find(m => m.id === value)?.vision) ?? false;
        onUpdate('local_cluster_config', { ...config, model: value, vision_capable: visionCapable });
    };

    const handleLoadModels = useCallback(async () => {
        setIsFetchingModels(true);
        setFetchError(null);
        try {
            const apiKey = config.api_key || '';
            const url = `${process.env.REACT_APP_API_URL}/get_local_cluster_models` +
                (apiKey && !apiKey.startsWith('*') ? `?api_key=${encodeURIComponent(apiKey)}` : '');
            const resp = await fetch(url, {
                headers: { Authorization: `Bearer ${accessToken}` }
            });
            const data = await resp.json();
            if (data.status && data.models?.length > 0) {
                setLocalModels(data.models);
                if (!config.model) {
                    const first = data.models[0];
                    onUpdate('local_cluster_config', { ...config, model: first.id, vision_capable: first.vision });
                }
            } else {
                const errMap = {
                    CLUSTER_TIMEOUT: tConfig('local_cluster_timeout'),
                    CLUSTER_UNREACHABLE: tConfig('local_cluster_unreachable'),
                };
                setFetchError(errMap[data.error] || tConfig('local_cluster_fetch_error'));
                setLocalModels([]);
            }
        } catch {
            setFetchError(tConfig('local_cluster_fetch_error'));
            setLocalModels([]);
        } finally {
            setIsFetchingModels(false);
        }
    }, [accessToken, config, onUpdate, tConfig]);

    // Se ainda não carregou da API mas já temos um modelo guardado, mostramos esse como fallback
    const options = localModels.length > 0
        ? localModels
        : config.model
            ? [{ id: config.model, vision: false }]
            : [];

    const selectedVision = options.find(m => m.id === config.model)?.vision ?? false;

    return (
        <div className="local-cluster-config">
            <div className="local-cluster-field">
                <label>{tConfig('local_cluster_api_key_label')}</label>
                <div className="local-cluster-api-key-wrapper">
                    <input
                        type={showApiKey ? 'text' : 'password'}
                        placeholder={tConfig('local_cluster_api_key_placeholder')}
                        value={config.api_key || ''}
                        onChange={(e) => handleApiKeyChange(e.target.value)}
                        autoComplete="new-password"
                    />
                    <button
                        type="button"
                        className="local-cluster-toggle-btn"
                        onClick={() => setShowApiKey(v => !v)}
                    >
                        {showApiKey ? tConfig('local_cluster_hide_key') : tConfig('local_cluster_show_key')}
                    </button>
                </div>
            </div>

            <div className="local-cluster-field">
                <label>
                    {tConfig('local_cluster_model_label')}
                    {selectedVision && (
                        <span className="local-cluster-vision-badge" title={tConfig('local_cluster_vision_capable')}>
                            👁 {tConfig('local_cluster_vision_capable')}
                        </span>
                    )}
                </label>
                <div className="local-cluster-model-row">
                    <select
                        value={config.model || ''}
                        onChange={(e) => handleModelChange(e.target.value)}
                        disabled={options.length === 0}
                    >
                        {options.length === 0 && (
                            <option value="">{tConfig('local_cluster_model_placeholder')}</option>
                        )}
                        {options.map((m) => (
                            <option key={m.id} value={m.id}>
                                {m.vision ? `👁 ${m.id}` : m.id}
                            </option>
                        ))}
                    </select>
                    <button
                        type="button"
                        className="local-cluster-load-btn"
                        onClick={handleLoadModels}
                        disabled={isFetchingModels}
                    >
                        {isFetchingModels ? tConfig('local_cluster_loading') : tConfig('local_cluster_load_models')}
                    </button>
                </div>
                {fetchError && <p className="local-cluster-error">{fetchError}</p>}
            </div>
        </div>
    );
}

export default LocalClusterConfig;
